import { useState, useEffect, useCallback, useRef } from 'react';
import { auth } from '../services/firebase';
import {
  saveAgendaEvent,
  deleteAgendaEvent as deleteAgendaEventFirestore,
  subscribeToAgendaEvents,
  saveTask as saveTaskFirestore,
  deleteTask as deleteTaskFirestore,
  subscribeToTasks,
  saveCompletedState,
  subscribeToCompletedState,
  isAuthenticated
} from '../services/firestoreSync';
import {
  createCalendarEvent,
  updateCalendarEvent,
  deleteCalendarEvent,
  isCalendarAvailable
} from '../services/googleCalendarSync';
import { getDeviceId } from '../services/conflictService';
import { enqueue as enqueueOffline } from '../services/syncQueueStore';

// Hook personnalisé pour persister les données dans localStorage
export function useLocalStorage(key, initialValue) {
  // Récupérer la valeur depuis localStorage ou utiliser la valeur initiale
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Erreur lecture localStorage pour ${key}:`, error);
      return initialValue;
    }
  });

  // Sauvegarder dans localStorage à chaque changement
  useEffect(() => {
    try {
      window.localStorage.setItem(key, JSON.stringify(storedValue));
    } catch (error) {
      console.error(`Erreur écriture localStorage pour ${key}:`, error);
    }
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}

// Hook pour gérer les tâches personnalisées avec sync Firestore
export function useCustomTasks() {
  const [localTasks, setLocalTasks] = useLocalStorage('adhd-planner-custom-tasks', []);
  const [tasks, setTasksState] = useState(localTasks);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const unsubscribeRef = useRef(null);
  const isSubscribedRef = useRef(false);

  // Sync tasks state with localStorage as fallback
  useEffect(() => {
    setTasksState(localTasks);
  }, [localTasks]);

  // Subscribe to Firestore real-time updates when authenticated
  useEffect(() => {
    const setupSubscription = () => {
      if (!isAuthenticated()) {
        console.log('📴 Tasks: Mode hors-ligne (non connecté)');
        return;
      }

      if (isSubscribedRef.current) return;

      try {
        console.log('🔄 Tasks: Connexion temps réel Firestore...');
        isSubscribedRef.current = true;

        unsubscribeRef.current = subscribeToTasks(
          (firestoreTasks) => {
            console.log('📥 Tasks: Reçu', firestoreTasks.length, 'tâches');
            setTasksState(firestoreTasks);
            // Also update localStorage as cache
            setLocalTasks(firestoreTasks);
            setSyncError(null);
          },
          (error) => {
            console.error('❌ Tasks sync error:', error);
            setSyncError(error.message);
            isSubscribedRef.current = false;
          }
        );
      } catch (error) {
        console.error('Error setting up tasks subscription:', error);
        isSubscribedRef.current = false;
      }
    };

    // Listen for auth state changes
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setupSubscription();
      } else {
        // Unsubscribe when logged out
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        isSubscribedRef.current = false;
        // Local tasks already synced via the separate useEffect above
      }
    });

    // Initial setup
    setupSubscription();

    return () => {
      unsubscribeAuth();
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      isSubscribedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save task to Firestore (if authenticated) or queue for later
  const saveToFirestore = useCallback(async (task) => {
    if (isAuthenticated()) {
      try {
        setIsSyncing(true);
        await saveTaskFirestore(task);
      } catch (error) {
        console.error('Error saving task to Firestore:', error);
        setSyncError(error.message);
        // Queue for retry when next online
        enqueueOffline({ type: 'save', entityType: 'task', entityId: task.id, data: task });
      } finally {
        setIsSyncing(false);
      }
    } else {
      // Offline — queue for when connection is restored
      enqueueOffline({ type: 'save', entityType: 'task', entityId: task.id, data: task });
    }
  }, []);

  const addTask = useCallback((task) => {
    const newTask = {
      ...task,
      id: task.id || `custom-${Date.now()}`,
      createdAt: new Date().toISOString(),
      version: 1,
      deviceId: getDeviceId(),
    };

    // Anti-doublon: si une tâche avec le même ID ou googleEventId existe déjà, skip
    const isDuplicate = tasks.some(t =>
      t.id === newTask.id ||
      (newTask.googleEventId && t.googleEventId === newTask.googleEventId)
    );
    if (isDuplicate) return newTask;

    // Update local state immediately
    setTasksState(prev => [...prev, newTask]);
    setLocalTasks(prev => [...prev, newTask]);

    // Sync to Firestore (or queue if offline)
    saveToFirestore(newTask);

    // Sync to Google Calendar (async, non-blocking)
    if (isCalendarAvailable() && newTask.date && !newTask.isImported) {
      const taskDate = new Date(newTask.date + 'T00:00:00');
      createCalendarEvent(newTask, taskDate).catch(err => {
        console.warn('Google Calendar sync error:', err);
      });
    }

    return newTask;
  }, [tasks, saveToFirestore, setLocalTasks]);

  const updateTask = useCallback((taskId, updates) => {
    const existingTask = tasks.find(t => t.id === taskId);
    const updatedTask = {
      ...existingTask,
      ...updates,
      updatedAt: new Date().toISOString(),
      version: (existingTask?.version ?? 0) + 1,
      deviceId: getDeviceId(),
    };

    // Update local state immediately
    setTasksState(prev => prev.map(t =>
      t.id === taskId ? updatedTask : t
    ));
    setLocalTasks(prev => prev.map(t =>
      t.id === taskId ? updatedTask : t
    ));

    // Sync to Firestore
    saveToFirestore(updatedTask);

    // Sync to Google Calendar (async, non-blocking)
    if (isCalendarAvailable() && updatedTask.date) {
      const taskDate = new Date(updatedTask.date + 'T00:00:00');
      updateCalendarEvent(updatedTask, taskDate).catch(err => {
        console.warn('Google Calendar update error:', err);
      });
    }
  }, [tasks, saveToFirestore, setLocalTasks]);

  const deleteTask = useCallback(async (taskId) => {
    // Get the task before deleting to check for Google Calendar ID
    const taskToDelete = tasks.find(t => t.id === taskId);

    // Update local state immediately
    setTasksState(prev => prev.filter(t => t.id !== taskId));
    setLocalTasks(prev => prev.filter(t => t.id !== taskId));

    // Delete from Firestore (or queue if offline)
    if (isAuthenticated()) {
      try {
        setIsSyncing(true);
        await deleteTaskFirestore(taskId);
      } catch (error) {
        console.error('Error deleting task from Firestore:', error);
        setSyncError(error.message);
        enqueueOffline({ type: 'delete', entityType: 'task', entityId: taskId });
      } finally {
        setIsSyncing(false);
      }
    } else {
      enqueueOffline({ type: 'delete', entityType: 'task', entityId: taskId });
    }

    // Delete from Google Calendar (async, non-blocking)
    if (isCalendarAvailable()) {
      deleteCalendarEvent(taskId, taskToDelete?.googleEventId).catch(err => {
        console.warn('Google Calendar delete error:', err);
      });
    }
  }, [tasks, setLocalTasks]);

  const setTasks = useCallback((newTasksOrUpdater) => {
    const newTasks = typeof newTasksOrUpdater === 'function'
      ? newTasksOrUpdater(tasks)
      : newTasksOrUpdater;

    // Update local state
    setTasksState(newTasks);
    setLocalTasks(newTasks);

    // Sync each task to Firestore
    if (isAuthenticated()) {
      newTasks.forEach(task => {
        saveToFirestore(task);
      });
    }
  }, [tasks, saveToFirestore, setLocalTasks]);

  const getTasksForDate = useCallback((dateStr) => {
    return tasks.filter(t => t.date === dateStr);
  }, [tasks]);

  const clearAllTasks = useCallback(() => {
    setTasksState([]);
    setLocalTasks([]);
    // Note: Firestore clear would need batch delete - implement if needed
  }, [setLocalTasks]);

  return {
    tasks,
    setTasks,
    addTask,
    updateTask,
    deleteTask,
    getTasksForDate,
    clearAllTasks,
    // Sync status
    isSyncing,
    syncError,
    isOnline: isAuthenticated(),
  };
}

// Helper pour calculer la durée en minutes
const calculateDuration = (start, end) => {
  if (!start || !end) return 60;
  const [startH, startM] = start.split(':').map(Number);
  const [endH, endM] = end.split(':').map(Number);
  return (endH * 60 + endM) - (startH * 60 + startM);
};

// Hook pour gérer les événements de l'agenda hebdomadaire avec sync Firestore
export function useAgendaEvents() {
  const [localEvents, setLocalEvents] = useLocalStorage('adhd-planner-agenda-events', []);
  const [hasSetupAgenda, setHasSetupAgenda] = useLocalStorage('adhd-planner-agenda-setup-done', false);
  const [energyLevel, setEnergyLevel] = useLocalStorage('adhd-planner-energy-level', 3);
  const [events, setEventsState] = useState(localEvents);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const unsubscribeRef = useRef(null);
  const isSubscribedRef = useRef(false);

  // Sync events state with localStorage as fallback
  useEffect(() => {
    setEventsState(localEvents);
  }, [localEvents]);

  // Subscribe to Firestore real-time updates when authenticated
  useEffect(() => {
    const setupSubscription = () => {
      if (!isAuthenticated()) {
        console.log('📴 Agenda: Mode hors-ligne (non connecté)');
        return;
      }

      if (isSubscribedRef.current) return;

      try {
        console.log('🔄 Agenda: Connexion temps réel Firestore...');
        isSubscribedRef.current = true;

        unsubscribeRef.current = subscribeToAgendaEvents(
          (firestoreEvents) => {
            console.log('📥 Agenda: Reçu', firestoreEvents.length, 'événements');
            setEventsState(firestoreEvents);
            // Also update localStorage as cache
            setLocalEvents(firestoreEvents);
            setSyncError(null);
          },
          (error) => {
            console.error('❌ Agenda sync error:', error);
            setSyncError(error.message);
            isSubscribedRef.current = false;
          }
        );
      } catch (error) {
        console.error('Error setting up subscription:', error);
        isSubscribedRef.current = false;
      }
    };

    // Listen for auth state changes
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setupSubscription();
      } else {
        // Unsubscribe when logged out
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        isSubscribedRef.current = false;
        // Local events already synced via the separate useEffect above
      }
    });

    // Initial setup
    setupSubscription();

    return () => {
      unsubscribeAuth();
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      isSubscribedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save event to Firestore (if authenticated) or queue for later
  const saveToFirestore = useCallback(async (event) => {
    if (isAuthenticated()) {
      try {
        setIsSyncing(true);
        await saveAgendaEvent(event);
      } catch (error) {
        console.error('Error saving to Firestore:', error);
        setSyncError(error.message);
        enqueueOffline({ type: 'save', entityType: 'event', entityId: event.id, data: event });
      } finally {
        setIsSyncing(false);
      }
    } else {
      enqueueOffline({ type: 'save', entityType: 'event', entityId: event.id, data: event });
    }
  }, []);

  const addEvent = useCallback((event) => {
    const newEvent = {
      ...event,
      id: event.id || `event-${Date.now()}`,
      createdAt: new Date().toISOString(),
      subTasks: event.subTasks || [],
      version: 1,
      deviceId: getDeviceId(),
    };

    // Anti-doublon: skip si un événement avec le même ID ou googleEventId existe
    const isDuplicate = events.some(e =>
      e.id === newEvent.id ||
      (newEvent.googleEventId && e.googleEventId === newEvent.googleEventId) ||
      (newEvent.linkedTaskId && e.linkedTaskId === newEvent.linkedTaskId)
    );
    if (isDuplicate) return newEvent;

    // Update local state immediately
    setEventsState(prev => [...prev, newEvent]);
    setLocalEvents(prev => [...prev, newEvent]);

    // Sync to Firestore
    saveToFirestore(newEvent);

    // Sync to Google Calendar (async, non-blocking)
    if (isCalendarAvailable() && newEvent.date && !newEvent.isImported) {
      const eventDate = new Date(newEvent.date + 'T00:00:00');
      // Convert agenda event to task format for Calendar API
      const taskFormat = {
        id: newEvent.id,
        task: newEvent.title,
        time: newEvent.start,
        duration: calculateDuration(newEvent.start, newEvent.end),
        category: newEvent.category,
        desc: '',
        instructions: newEvent.subTasks || []
      };
      createCalendarEvent(taskFormat, eventDate).catch(err => {
        console.warn('Google Calendar sync error:', err);
      });
    }

    return newEvent;
  }, [events, saveToFirestore, setLocalEvents]);

  const updateEvent = useCallback((eventId, updates) => {
    const existingEvent = events.find(e => e.id === eventId);
    const updatedEvent = {
      ...existingEvent,
      ...updates,
      updatedAt: new Date().toISOString(),
      version: (existingEvent?.version ?? 0) + 1,
      deviceId: getDeviceId(),
    };

    // Update local state immediately
    setEventsState(prev => prev.map(e =>
      e.id === eventId ? updatedEvent : e
    ));
    setLocalEvents(prev => prev.map(e =>
      e.id === eventId ? updatedEvent : e
    ));

    // Sync to Firestore
    saveToFirestore(updatedEvent);

    // Sync to Google Calendar (async, non-blocking)
    if (isCalendarAvailable() && updatedEvent.date) {
      const eventDate = new Date(updatedEvent.date + 'T00:00:00');
      const taskFormat = {
        id: updatedEvent.id,
        task: updatedEvent.title,
        time: updatedEvent.start,
        duration: calculateDuration(updatedEvent.start, updatedEvent.end),
        category: updatedEvent.category,
        desc: '',
        instructions: updatedEvent.subTasks || [],
        googleEventId: updatedEvent.googleEventId
      };
      updateCalendarEvent(taskFormat, eventDate).catch(err => {
        console.warn('Google Calendar update error:', err);
      });
    }
  }, [events, saveToFirestore, setLocalEvents]);

  const deleteEvent = useCallback(async (eventId) => {
    // Get the event before deleting to check for Google Calendar ID
    const eventToDelete = events.find(e => e.id === eventId);

    // Update local state immediately
    setEventsState(prev => prev.filter(e => e.id !== eventId));
    setLocalEvents(prev => prev.filter(e => e.id !== eventId));

    // Delete from Firestore (or queue if offline)
    if (isAuthenticated()) {
      try {
        setIsSyncing(true);
        await deleteAgendaEventFirestore(eventId);
      } catch (error) {
        console.error('Error deleting from Firestore:', error);
        setSyncError(error.message);
        enqueueOffline({ type: 'delete', entityType: 'event', entityId: eventId });
      } finally {
        setIsSyncing(false);
      }
    } else {
      enqueueOffline({ type: 'delete', entityType: 'event', entityId: eventId });
    }

    // Delete from Google Calendar (async, non-blocking)
    if (isCalendarAvailable()) {
      deleteCalendarEvent(eventId, eventToDelete?.googleEventId).catch(err => {
        console.warn('Google Calendar delete error:', err);
      });
    }
  }, [events, setLocalEvents]);

  const setEvents = useCallback(async (newEventsOrUpdater) => {
    const newEvents = typeof newEventsOrUpdater === 'function'
      ? newEventsOrUpdater(events)
      : newEventsOrUpdater;

    // Update local state immediately
    setEventsState(newEvents);
    setLocalEvents(newEvents);

    // Smart sync: only save changed/new events
    if (isAuthenticated()) {
      const oldIds = new Set(events.map(e => e.id));
      const newIds = new Set(newEvents.map(e => e.id));

      // Find new and updated events
      const toSync = newEvents.filter(event => {
        if (!oldIds.has(event.id)) return true; // New event
        const oldEvent = events.find(e => e.id === event.id);
        return JSON.stringify(oldEvent) !== JSON.stringify(event); // Changed
      });

      // Find deleted events
      const toDelete = events.filter(e => !newIds.has(e.id));

      // Sync in background
      setIsSyncing(true);
      try {
        await Promise.all([
          ...toSync.map(event => saveAgendaEvent(event)),
          ...toDelete.map(event => deleteAgendaEventFirestore(event.id))
        ]);
        console.log(`✅ Sync: ${toSync.length} saved, ${toDelete.length} deleted`);
      } catch (error) {
        console.error('Sync error:', error);
        setSyncError(error.message);
      } finally {
        setIsSyncing(false);
      }
    }
  }, [events, setLocalEvents]);

  const getEventsForDate = useCallback((dateStr) => {
    return events.filter(e => e.date === dateStr);
  }, [events]);

  const clearAllEvents = useCallback(async () => {
    const oldEvents = [...events];
    setEventsState([]);
    setLocalEvents([]);

    // Delete all from Firestore
    if (isAuthenticated() && oldEvents.length > 0) {
      setIsSyncing(true);
      try {
        await Promise.all(oldEvents.map(e => deleteAgendaEventFirestore(e.id)));
        console.log(`🗑️ ${oldEvents.length} événements supprimés de Firestore`);
      } catch (error) {
        console.error('Error clearing events:', error);
        setSyncError(error.message);
      } finally {
        setIsSyncing(false);
      }
    }
  }, [events, setLocalEvents]);

  const markSetupDone = useCallback(() => {
    setHasSetupAgenda(true);
  }, [setHasSetupAgenda]);

  const resetSetup = useCallback(() => {
    setHasSetupAgenda(false);
  }, [setHasSetupAgenda]);

  return {
    events,
    setEvents,
    addEvent,
    updateEvent,
    deleteEvent,
    getEventsForDate,
    clearAllEvents,
    hasSetupAgenda,
    markSetupDone,
    resetSetup,
    energyLevel,
    setEnergyLevel,
    // Sync status
    isSyncing,
    syncError,
    isOnline: isAuthenticated(),
  };
}

// Hook pour gérer les tâches complétées avec sync Firestore
export function useCompletedTasks() {
  const [localCompleted, setLocalCompleted] = useLocalStorage('adhd-planner-completed', {});
  const [completed, setCompletedState] = useState(localCompleted);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const unsubscribeRef = useRef(null);
  const isSubscribedRef = useRef(false);
  const pendingSaveRef = useRef(null);

  // Sync completed state with localStorage as fallback
  useEffect(() => {
    setCompletedState(localCompleted);
  }, [localCompleted]);

  // Subscribe to Firestore real-time updates when authenticated
  useEffect(() => {
    const setupSubscription = () => {
      if (!isAuthenticated()) {
        console.log('📴 Completed: Mode hors-ligne (non connecté)');
        return;
      }

      if (isSubscribedRef.current) return;

      try {
        console.log('🔄 Completed: Connexion temps réel Firestore...');
        isSubscribedRef.current = true;

        unsubscribeRef.current = subscribeToCompletedState(
          (firestoreCompleted) => {
            console.log('📥 Completed: Reçu état de complétion');
            setCompletedState(firestoreCompleted);
            // Also update localStorage as cache
            setLocalCompleted(firestoreCompleted);
            setSyncError(null);
          },
          (error) => {
            console.error('❌ Completed sync error:', error);
            setSyncError(error.message);
            isSubscribedRef.current = false;
          }
        );
      } catch (error) {
        console.error('Error setting up completed subscription:', error);
        isSubscribedRef.current = false;
      }
    };

    // Listen for auth state changes
    const unsubscribeAuth = auth.onAuthStateChanged((user) => {
      if (user) {
        setupSubscription();
      } else {
        // Unsubscribe when logged out
        if (unsubscribeRef.current) {
          unsubscribeRef.current();
          unsubscribeRef.current = null;
        }
        isSubscribedRef.current = false;
        // Local completed already synced via the separate useEffect above
      }
    });

    // Initial setup
    setupSubscription();

    return () => {
      unsubscribeAuth();
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
      isSubscribedRef.current = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced save to Firestore
  const saveToFirestore = useCallback((newCompleted) => {
    // Clear any pending save
    if (pendingSaveRef.current) {
      clearTimeout(pendingSaveRef.current);
    }

    // Debounce saves to reduce Firestore writes
    pendingSaveRef.current = setTimeout(async () => {
      if (isAuthenticated()) {
        try {
          setIsSyncing(true);
          await saveCompletedState(newCompleted);
          console.log('✅ État de complétion sauvegardé');
        } catch (error) {
          console.error('Error saving completed state to Firestore:', error);
          setSyncError(error.message);
        } finally {
          setIsSyncing(false);
        }
      }
    }, 300); // 300ms debounce
  }, []);

  const setCompleted = useCallback((updater) => {
    const newCompleted = typeof updater === 'function'
      ? updater(completed)
      : updater;

    // Update local state immediately
    setCompletedState(newCompleted);
    setLocalCompleted(newCompleted);

    // Sync to Firestore (debounced)
    saveToFirestore(newCompleted);
  }, [completed, setLocalCompleted, saveToFirestore]);

  const toggleTask = useCallback((weekOffset, dayIndex, taskId) => {
    const key = `${weekOffset}-${dayIndex}-${taskId}`;
    setCompleted(prev => ({ ...prev, [key]: !prev[key] }));
  }, [setCompleted]);

  const toggleSubTask = useCallback((weekOffset, dayIndex, taskId, subIndex) => {
    const key = `${weekOffset}-${dayIndex}-${taskId}-sub-${subIndex}`;
    setCompleted(prev => ({ ...prev, [key]: !prev[key] }));
  }, [setCompleted]);

  const isCompleted = useCallback((weekOffset, dayIndex, taskId) => {
    return !!completed[`${weekOffset}-${dayIndex}-${taskId}`];
  }, [completed]);

  const isSubTaskCompleted = useCallback((weekOffset, dayIndex, taskId, subIndex) => {
    return !!completed[`${weekOffset}-${dayIndex}-${taskId}-sub-${subIndex}`];
  }, [completed]);

  const getCompletedCount = useCallback((weekOffset, dayIndex, taskId, totalSubTasks) => {
    let count = 0;
    for (let i = 0; i < totalSubTasks; i++) {
      if (completed[`${weekOffset}-${dayIndex}-${taskId}-sub-${i}`]) count++;
    }
    return count;
  }, [completed]);

  // Réinitialiser pour une nouvelle semaine
  const clearWeek = useCallback((weekOffset) => {
    setCompleted(prev => {
      const newCompleted = {};
      Object.keys(prev).forEach(key => {
        if (!key.startsWith(`${weekOffset}-`)) {
          newCompleted[key] = prev[key];
        }
      });
      return newCompleted;
    });
  }, [setCompleted]);

  return {
    completed,
    toggleTask,
    toggleSubTask,
    isCompleted,
    isSubTaskCompleted,
    getCompletedCount,
    clearWeek,
    // Sync status
    isSyncing,
    syncError,
    isOnline: isAuthenticated(),
  };
}

// Hook pour les paramètres utilisateur
export function useSettings() {
  const [settings, setSettings] = useLocalStorage('adhd-planner-settings', {
    safetyMode: false,
    notificationsEnabled: false,
    darkMode: false,
    soundEnabled: true,
    googleCalendarSync: false,
  });

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const toggleSetting = (key) => {
    setSettings(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return { settings, updateSetting, toggleSetting };
}

export default useLocalStorage;
